import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import '../Styles/Personnel.css'; // common styles
import { FaUserCheck, FaSignOutAlt, FaBars, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { BarChart3, Users, Calendar, Folder, Grid3x3, Building2, Settings } from "lucide-react";
import talentLogo3 from "../assets/talentlogo3-removebg-preview.png";
import { BASE_URL } from '../config';
import useAuth from '../hooks/useAuth';
import { useAppPermissions } from '../context/PermissionContext';
import StaffAvatarCircle from "../components/StaffAvatarCircle";

/** module: null = always; personnel | client | shift | accounting | admin */
const NAV_DEFINITION = [
  { name: 'Dashboard', path: '/dashboard', icon: BarChart3, module: null },
  { name: 'Personnel', path: '/personnel', icon: Users, module: 'personnel' },
  { name: 'Clients', path: '/clients', icon: Building2, module: 'client' },
  { name: 'Jobs', path: '/jobs', icon: Folder, module: 'shift' },
  { name: 'Schedule', path: '/schedule', icon: Calendar, module: 'shift' },
  { name: 'Accounting', path: '/accounting', icon: Grid3x3, module: 'accounting' },
  { name: 'Admin',
    icon: Settings,
    module: 'admin',
    children: [
      { name: 'Accounts', path: '/admin/accounts'},
      { name: 'Settings', path: '/admin/settings'},
      { name: 'Messages', path: '/admin/messages'},
      { name: 'Audits', path: '/admin/audits'},
    ]
  },
];

function filterNavForPermissions(items, perms) {
  return items.filter((item) => {
    if (!item.module) return true;
    switch (item.module) {
      case 'personnel':
        return perms.canAccessPersonnel;
      case 'client':
        return perms.canReadClients;
      case 'shift':
        return perms.canReadShifts;
      case 'accounting':
        return perms.canReadAccounting;
      case 'admin':
        return perms.canReadAdmin;
      default:
        return true;
    }
  });
}

const Layout = ({ children, active }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const perms = useAppPermissions();
  const sidebarItems = useMemo(
    () => filterNavForPermissions(NAV_DEFINITION, perms),
    [perms]
  );
  const [profilePicture, setProfilePicture] = useState("");
  const [userName, setUserName] = useState("");

  // find if any of Admin's children matches current path
  const adminDef = sidebarItems.find(item => item.name === "Admin");
  const isChildActive = adminDef
    ?.children.some(child => location.pathname.startsWith(child.path));
  const [isAdminOpen, setIsAdminOpen] = useState(isChildActive || false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Fetch profile picture
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const accountId = auth?.accountId || localStorage.getItem("accountId");
        const token = auth?.token || localStorage.getItem("token");
        if (!accountId) return;

        const res = await fetch(`${BASE_URL}/api/my-account/account/me`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });

        const json = await res.json();
        if (json.success && json.data) {
          const d = json.data;
          setProfilePicture(d.profile_picture || "");
          const fn = d.first_name || "";
          const ln = d.last_name || "";
          setUserName(`${fn} ${ln}`.trim() || "Admin");
        }
      } catch (e) {
        console.error("Failed to load profile", e);
      }
    };

    if (auth?.accountId || localStorage.getItem("accountId")) {
      loadProfile();
    }
  }, [auth?.accountId]);

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (userName) {
      const parts = userName.split(" ").filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return parts[0]?.slice(0, 2).toUpperCase() || "CX";
    }
    return "CX";
  };

  // Get current page title
  const getCurrentPageTitle = () => {
    // Check if it's an admin child page
    const adminItem = sidebarItems.find(item => item.name === "Admin");
    if (adminItem) {
      const childPage = adminItem.children.find(child => location.pathname.startsWith(child.path));
      if (childPage) return childPage.name;
    }
    
    // Check main pages
    const mainPage = sidebarItems.find(item => item.path && location.pathname.startsWith(item.path));
    if (mainPage) return mainPage.name;
    
    return active || 'Dashboard';
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("accountId");
    localStorage.removeItem("isAdmin");
    window.dispatchEvent(new Event('authUpdate'));
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Mobile Header (also covers tablet/iPad widths, which are too narrow for the persistent sidebar) */}
      <div className="lg:hidden bg-sidebar-bg text-sidebar-text px-4 py-3 flex items-center justify-between timesheet-print-hide">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1 hover:bg-sidebar-hover rounded"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsMobileSidebarOpen(true)}
            className="p-1 hover:bg-sidebar-hover rounded"
          >
            <FaBars className="w-4 h-4" />
          </button>
          <h1 className="text-lg font-semibold">{getCurrentPageTitle()}</h1>
        </div>
        <img src={talentLogo3} alt="Talent Logo" className="h-8 w-auto" />
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsMobileSidebarOpen(false)}></div>
          <div className="absolute left-0 top-0 h-full w-80 bg-sidebar-bg text-sidebar-text flex flex-col">
            {/* Mobile Sidebar Header */}
            <div className="p-4 flex items-center justify-between border-b border-sidebar-hover">
              <img src={talentLogo3} alt="Talent Logo" className="h-10 w-auto" />
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                className="p-1 hover:bg-sidebar-hover rounded"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            {/* Mobile Navigation */}
            <div className="flex-1 overflow-hidden">
              <nav className="h-full overflow-y-auto inner-scroll px-4 pr-2 py-4">
                {sidebarItems.map((item) => {
                  const IconComponent = item.icon;
                  
                  if (item.children) {
                    return (
                      <div key={item.name}>
                        <button
                          onClick={() => {
                            setIsAdminOpen(true);
                            navigate(item.children[0].path);
                            setIsMobileSidebarOpen(false);
                          }}
                          className={`w-full flex items-center px-4 py-2 mb-0.5 rounded-lg transition-colors ${
                            active === item.name
                              ? 'bg-sidebar-active text-white'
                              : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
                          }`}
                        >
                          {IconComponent && <IconComponent className="w-4 h-4 mr-3" />}
                          <span className="text-sm font-medium">{item.name}</span>
                        </button>

                        {isAdminOpen && (
                          <div className="ml-4">
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.path}
                                onClick={() => setIsMobileSidebarOpen(false)}
                                className={`flex items-center px-4 py-1.5 mb-0.5 rounded-lg transition-colors ${
                                  active === child.name
                                    ? 'bg-sidebar-active text-white'
                                    : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
                                }`}
                              >
                                <span className="text-sm ml-8">{child.name}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setIsMobileSidebarOpen(false)}
                      className={`flex items-center px-4 py-2 mb-0.5 rounded-lg transition-colors ${
                        active === item.name
                          ? 'bg-sidebar-active text-white'
                          : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
                      }`}
                    >
                      {IconComponent && <IconComponent className="w-4 h-4 mr-3" />}
                      <span className="text-sm font-medium">{item.name}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
            
            {/* Mobile Footer */}
            <div className="p-4 border-t border-sidebar-hover">
              <Link to="/my-account" onClick={() => setIsMobileSidebarOpen(false)}>
                <div className="flex items-center space-x-2 mb-2">
                  <StaffAvatarCircle
                    accountId={auth?.accountId || localStorage.getItem("accountId")}
                    employee={{
                      account_id: auth?.accountId || localStorage.getItem("accountId"),
                      profile_picture: profilePicture,
                      name: userName,
                      initials: getUserInitials(),
                    }}
                    className="w-8 h-8"
                    textClassName="text-xs font-medium text-white"
                    emptyLabel={getUserInitials()}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm text-sidebar-text-muted">My Account</span>
                    <span className="text-xs text-sidebar-text-muted">{userName || "User"}</span>
                  </div>
                </div>
              </Link>
              <button
                onClick={() => {
                  handleLogout();
                  setIsMobileSidebarOpen(false);
                }}
                className="flex items-center px-4 py-2 text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text rounded-lg transition-colors w-full"
              >
                <FaSignOutAlt className="mr-2 font-medium text-sidebar-text-muted" size={18} />
                <span className="text-sm font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar (lg and up only — iPad/tablet widths use the mobile drawer above instead) */}
      <div className="sidebar relative z-20 hidden lg:flex w-[12rem] bg-sidebar-bg text-sidebar-text flex-col h-screen sticky top-0 flex-shrink-0 timesheet-print-hide">
        {/* Header */}
        <div className="p-6">
          <div className="flex items-center justify-center">
            <img src={talentLogo3} alt="Talent Logo" className="h-12 w-auto" />
          </div>
        </div>

        {/* Nav: scrollable area only */}
        <div className="flex-1 overflow-hidden">
          <nav className="h-full overflow-y-auto inner-scroll px-4 pr-2">
            {sidebarItems.map((item) => {
              const IconComponent = item.icon;
              
              if (item.children) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => {
                        setIsAdminOpen(true);
                        navigate(item.children[0].path);
                      }}
                      className={`w-full flex items-center px-4 py-2 mb-0.5 rounded-lg transition-colors ${
                        active === item.name
                          ? 'bg-sidebar-active text-white'
                          : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
                      }`}
                    >
                      {IconComponent && <IconComponent className="w-4 h-4 mr-3" />}
                      <span className="text-sm font-medium">{item.name}</span>
                    </button>

                    {isAdminOpen && (
                      <div className="ml-4">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.path}
                            className={`flex items-center px-4 py-1.5 mb-0.5 rounded-lg transition-colors ${
                              active === child.name
                                ? 'bg-sidebar-active text-white'
                                : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
                            }`}
                          >
                            <span className="text-sm ml-8">{child.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center px-4 py-2 mb-0.5 rounded-lg transition-colors ${
                    active === item.name
                      ? 'bg-sidebar-active text-white'
                      : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
                  }`}
                >
                  {IconComponent && <IconComponent className="w-4 h-4 mr-3" />}
                  <span className="text-sm font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-sidebar-hover">
          <Link to="/my-account">
            <div className="flex items-center space-x-2">
              <StaffAvatarCircle
                accountId={auth?.accountId || localStorage.getItem("accountId")}
                employee={{
                  account_id: auth?.accountId || localStorage.getItem("accountId"),
                  profile_picture: profilePicture,
                  name: userName,
                  initials: getUserInitials(),
                }}
                className="w-8 h-8"
                textClassName="text-xs font-medium text-white"
                emptyLabel={getUserInitials()}
              />
              <div className="flex flex-col">
                <span className="text-sm text-sidebar-text-muted">My Account</span>
                <span className="text-xs text-sidebar-text-muted">{userName || "Admin"}</span>
              </div>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 mt-2 text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text rounded-lg transition-colors w-full"
          >
            <FaSignOutAlt className="mr-2 font-medium text-sidebar-text-muted" size={18} />
            <span className="text-sm font-medium">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-x-hidden overflow-y-hidden timesheet-print-overflow-visible">
        {children}
      </div>
    </div>
  );
};

export default Layout;
